/**
 * CitationLoadingAnimation — Loading state for citation sidebar.
 *
 * Phase 1 (0–1s): Simple centered spinner
 * Phase 2 (1s+): Wireframe globe with orbiting masked IPs
 * Phase 3 (complete): One IP turns green with checkmark, others fade
 *
 * Props:
 *   isComplete (bool)    — triggers success flash
 *   statusMessage (str)  — text shown below the globe
 *   onCompleteFinished() — called after success animation ends (~600ms)
 */

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check } from "lucide-react";

const APP_BLUE = "#1c9dea";
const SUCCESS_GREEN = "#22c55e";

/* ── Random IP generator ── */
const randomOctet = () => Math.floor(Math.random() * 256);
const maskOctet = () => (Math.random() > 0.5 ? "XXX" : `${randomOctet()}`);

const generateIP = () => {
  const parts = [randomOctet(), maskOctet(), randomOctet(), maskOctet()];
  if (!parts.includes("XXX")) parts[1] = "XXX";
  return parts.join(".");
};

const generateIPs = () => {
  const count = 3 + Math.floor(Math.random() * 3);
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    ip: generateIP(),
    duration: 6 + Math.random() * 4,
    radiusX: 72 + Math.random() * 20,
    radiusY: 28 + Math.random() * 12,
    startAngle: (i / count) * Math.PI * 2,
    yOffset: -10 + Math.random() * 20,
  }));
};

/* ── Wireframe Globe (SVG) ── */
const WireframeGlobe = ({ size = 130 }) => {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;

  return (
    <motion.svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      animate={{ rotate: 360 }}
      transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      style={{ opacity: 0.25 }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={APP_BLUE}
        strokeWidth={0.8}
      />
      {[-0.5, 0, 0.5].map((offset, i) => (
        <ellipse
          key={`lat-${i}`}
          cx={cx}
          cy={cy + offset * r * 0.85}
          rx={r * Math.sqrt(1 - offset * offset * 0.72)}
          ry={r * 0.12}
          fill="none"
          stroke={APP_BLUE}
          strokeWidth={0.5}
          opacity={0.6}
        />
      ))}
      {[0, 60, 120].map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        const rx = r * Math.abs(Math.cos(rad)) * 0.35 + r * 0.08;
        return (
          <ellipse
            key={`lon-${i}`}
            cx={cx}
            cy={cy}
            rx={rx}
            ry={r}
            fill="none"
            stroke={APP_BLUE}
            strokeWidth={0.5}
            opacity={0.4}
            transform={`rotate(${deg - 90} ${cx} ${cy})`}
          />
        );
      })}
    </motion.svg>
  );
};

/* ── Single orbiting IP pill ── */
const OrbitingIP = ({ ip, config, isSuccess }) => {
  const { duration, radiusX, radiusY, startAngle, yOffset } = config;

  const steps = 60;
  const xFrames = [];
  const yFrames = [];
  const opacityFrames = [];

  for (let s = 0; s <= steps; s++) {
    const angle = startAngle + (s / steps) * Math.PI * 2;
    xFrames.push(Math.cos(angle) * radiusX);
    yFrames.push(yOffset + Math.sin(angle) * radiusY);
    const behindness = Math.sin(angle);
    opacityFrames.push(behindness > 0.3 ? 0.2 : 1);
  }

  if (isSuccess) {
    return (
      <motion.div
        className="absolute flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono whitespace-nowrap"
        initial={{ scale: 1 }}
        animate={{ scale: [1, 1.15, 1], x: 0, y: -20 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{
          background: `${SUCCESS_GREEN}20`,
          border: `1px solid ${SUCCESS_GREEN}60`,
          color: SUCCESS_GREEN,
          zIndex: 20,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Check size={10} strokeWidth={2.5} />
        <span>{ip}</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="absolute flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono whitespace-nowrap pointer-events-none"
      animate={{ x: xFrames, y: yFrames, opacity: opacityFrames }}
      transition={{ duration, repeat: Infinity, ease: "linear" }}
      style={{
        background: "rgba(28,157,234,0.07)",
        border: "1px solid rgba(28,157,234,0.19)",
        color: APP_BLUE,
        left: "50%",
        top: "50%",
        marginLeft: -40,
        marginTop: -10,
      }}
    >
      {ip}
    </motion.div>
  );
};

/* ── Main component ── */
const CitationLoadingAnimation = ({
  isComplete = false,
  statusMessage = "Allocating a proxy…",
  onCompleteFinished,
}) => {
  const [extended, setExtended] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successIndex, setSuccessIndex] = useState(0);
  const ips = useMemo(() => generateIPs(), []);

  useEffect(() => {
    if (isComplete) {
      setExtended(true);
      return;
    }
    const timer = setTimeout(() => setExtended(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isComplete) return;
    setSuccessIndex(Math.floor(Math.random() * ips.length));
    setShowSuccess(true);
    const timer = setTimeout(() => {
      onCompleteFinished?.();
    }, 600);
    return () => clearTimeout(timer);
  }, [isComplete, ips.length, onCompleteFinished]);

  if (!extended) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-mutedText" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 select-none">
      <div className="relative" style={{ width: 180, height: 160 }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <WireframeGlobe size={130} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <AnimatePresence>
            {ips.map((ipData, i) => {
              if (showSuccess && i !== successIndex) {
                return (
                  <motion.div
                    key={ipData.id}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.3 }}
                  />
                );
              }
              return (
                <OrbitingIP
                  key={ipData.id}
                  ip={ipData.ip}
                  config={ipData}
                  isSuccess={showSuccess && i === successIndex}
                />
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <span
        className="text-xs font-medium"
        style={{ color: showSuccess ? SUCCESS_GREEN : "rgb(148,163,184)" }}
      >
        {showSuccess ? "Proxy secured" : statusMessage}
      </span>
    </div>
  );
};

export default CitationLoadingAnimation;
